import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { ImgDbUploadService } from 'src/app/img-db/services/img-db-upload.service';
import { UploadJob } from 'src/app/shared/models/upload-job';
import {
    BodyLine,
    ConfirmationDialogComponent,
    ConfirmationDialogData,
} from '../confirmation-modal/confirmation-dialog.component';

export type DisplayJobState = 'active' | 'completed' | 'failed' | 'cancelled';

export interface DisplayJob extends UploadJob {
    displayState: DisplayJobState;
}

@Component({
    selector: 'app-upload-queue-overlay',
    templateUrl: './upload-queue-overlay.component.html',
    styleUrls: ['./upload-queue-overlay.component.scss'],
    standalone: false,
})
export class UploadQueueOverlayComponent implements OnInit, OnDestroy {
    jobs: UploadJob[] = [];
    displayJobs: DisplayJob[] = [];
    isExpanded: boolean = true;
    isVisible: boolean = false;

    private subscriptions: Subscription[] = [];
    private autoMinimizeTimer: ReturnType<typeof setTimeout> | null = null;
    private autoHideTimer: ReturnType<typeof setTimeout> | null = null;
    private lastJobCount: number = 0;
    private lastActiveJobs: Map<string, UploadJob> = new Map();

    constructor(
        private uploadService: ImgDbUploadService,
        private dialog: MatDialog,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.uploadService.uploadJobs$.subscribe((jobs) => {
                const activeJobs = jobs.filter(
                    (job) =>
                        job.status === 'queued' ||
                        job.status === 'uploading' ||
                        job.status === 'processing'
                );
                const previousCount = this.lastJobCount;
                this.jobs = activeJobs;
                this.lastJobCount = activeJobs.length;

                // Track current state of all jobs for final state detection
                const currentJobMap = new Map(jobs.map((job) => [job.id, job]));

                // Detect jobs that were removed (completed or cancelled)
                const finishedJobs: DisplayJob[] = [];
                this.lastActiveJobs.forEach((lastJob, jobId) => {
                    const currentJob = currentJobMap.get(jobId);
                    if (!currentJob) {
                        // Job was removed - determine final state from last known state
                        const displayState =
                            this.determineDisplayState(lastJob);
                        finishedJobs.push({
                            ...lastJob,
                            displayState,
                        });
                    }
                });

                if (this.jobs.length === 0) {
                    // Hide overlay immediately when all jobs complete
                    if (previousCount > 0) {
                        this.isVisible = false;
                        this.isExpanded = false;
                        this.displayJobs = [];
                        this.lastActiveJobs.clear();
                    }
                    return;
                }

                // Show overlay when there are active jobs
                this.displayJobs = activeJobs.map((job) => ({
                    ...job,
                    displayState: 'active' as DisplayJobState,
                }));
                this.isVisible = true;

                // Update last active jobs map
                this.lastActiveJobs.clear();
                jobs.forEach((job) =>
                    this.lastActiveJobs.set(job.id, { ...job })
                );

                this.clearAutoHideTimer();

                if (
                    (!this.isExpanded || previousCount === 0) &&
                    this.jobs.length > previousCount
                ) {
                    this.expandTemporarily();
                }
            })
        );
    }

    private determineDisplayState(job: UploadJob): DisplayJobState {
        if (job.status === 'cancelled' || job.cancelRequested) {
            return 'cancelled';
        }
        const failedCount =
            job.invalidFormatCount +
            job.fileTooLargeCount +
            job.uploadFailedCount;
        if (failedCount > 0) {
            return 'failed';
        }
        return 'completed';
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.clearAutoMinimizeTimer();
        this.clearAutoHideTimer();
    }

    toggleExpanded(): void {
        if (this.displayJobs.length === 0) {
            return;
        }
        this.isExpanded = !this.isExpanded;
        if (this.isExpanded) {
            this.clearAutoMinimizeTimer();
        }
    }

    expandTemporarily(): void {
        this.isExpanded = true;
        this.clearAutoMinimizeTimer();
        this.autoMinimizeTimer = setTimeout(() => {
            this.isExpanded = false;
        }, 3000);
    }

    getUploadCount(): number {
        return this.displayJobs.length;
    }

    getOverlayTitle(): string {
        const count = this.displayJobs.length;
        if (count === 0) {
            return '';
        }
        return this.translateService.instant('imgDb.upload.overlay.title', {
            count,
        });
    }

    isJobCompleted(job: DisplayJob): boolean {
        return job.displayState !== 'active';
    }

    getProgressPercent(job: DisplayJob): number {
        if (!job.totalCount) {
            return 0;
        }
        return Math.min(
            100,
            Math.round((job.processedCount / job.totalCount) * 100)
        );
    }

    getTargetLabel(job: DisplayJob): string {
        if (job.target.type === 'training') {
            return this.translateService.instant(
                'imgDb.upload.target.training',
                {
                    trainingName: job.target.trainingName,
                    projectName: job.target.containerName,
                }
            );
        }
        return this.translateService.instant('imgDb.upload.target.imageDb', {
            imageDbName: job.target.imageDbName,
        });
    }

    getPlainTargetLabel(job: DisplayJob): string {
        return this.getTargetLabel(job).replace(/<[^>]*>/g, '');
    }

    getStatusLabel(job: DisplayJob): string {
        if (job.displayState === 'failed') {
            return this.translateService.instant(
                'imgDb.upload.overlay.status.failed'
            );
        }
        if (job.displayState === 'cancelled') {
            return this.translateService.instant(
                'imgDb.upload.overlay.status.cancelled'
            );
        }
        if (job.displayState !== 'active') {
            return '';
        }
        if (job.status === 'queued') {
            return this.translateService.instant(
                'imgDb.upload.overlay.status.queued'
            );
        }
        if (job.status === 'uploading') {
            return this.translateService.instant(
                'imgDb.upload.overlay.status.uploading'
            );
        }
        if (job.status === 'processing') {
            return this.translateService.instant(
                'imgDb.upload.overlay.status.processing'
            );
        }
        return '';
    }

    getQueuePosition(job: DisplayJob): number {
        if (job.status !== 'queued') {
            return 0;
        }
        const queuedJobs = this.displayJobs.filter(
            (j) => j.status === 'queued'
        );
        const index = queuedJobs.findIndex((j) => j.id === job.id);
        return index >= 0 ? index + 1 : 0;
    }

    trackByJobId(_index: number, job: DisplayJob): string {
        return job.id;
    }

    openCancelDialog(job: DisplayJob): void {
        let targetKey: string;
        let params: Record<string, string>;

        if (job.target.type === 'training') {
            targetKey = 'imgDb.upload.cancelDialog.target.training';
            params = {
                trainingName: job.target.trainingName,
                projectName: job.target.containerName,
            };
        } else {
            targetKey = 'imgDb.upload.cancelDialog.target.imageDb';
            params = { imageDbName: job.target.imageDbName };
        }

        const bodyLines: BodyLine[] = [
            {
                translationKey: targetKey,
                interpolateParameters: params,
                style: 'highlighted',
            },
            {
                translationKey: 'imgDb.upload.cancelDialog.body',
            },
        ];

        this.dialog
            .open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
                ConfirmationDialogComponent,
                {
                    data: {
                        headerTranslationKey:
                            'imgDb.upload.cancelDialog.header',
                        confirmationQuestion: {
                            translationKey:
                                'imgDb.upload.cancelDialog.question.intro',
                            interpolateParameters: {},
                        },
                        bodyLines,
                        confirmButtonTranslationKey:
                            'imgDb.upload.cancelDialog.confirm',
                        rejectButtonTranslationKey:
                            'imgDb.upload.cancelDialog.reject',
                        transferData: true,
                        iconPath:
                            'assets/svg/reusable-svg/icon-cancel-pink.svg',
                    } as ConfirmationDialogData,
                    width: '650px',
                    disableClose: true,
                }
            )
            .afterClosed()
            .subscribe((confirmed) => {
                if (confirmed) {
                    this.uploadService.requestCancel(job.id);
                }
            });
    }

    private clearAutoMinimizeTimer(): void {
        if (this.autoMinimizeTimer) {
            clearTimeout(this.autoMinimizeTimer);
            this.autoMinimizeTimer = null;
        }
    }

    private clearAutoHideTimer(): void {
        if (this.autoHideTimer) {
            clearTimeout(this.autoHideTimer);
            this.autoHideTimer = null;
        }
    }
}
